/**
 * GuruRecommendationsSection Component
 * 
 * Horizontal scrollable section with guru recommendation cards
 */

import React from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Text, Theme } from '../../../components/ui';
import { GuruRecommendationCard } from './GuruRecommendationCard';
import { GuruRecommendationWithBooks } from '../../../hooks/useExploreSections';

interface GuruRecommendationsSectionProps {
  title?: string;
  subtitle?: string;
  recommendations: GuruRecommendationWithBooks[];
  onGuruPress?: (guru: GuruRecommendationWithBooks) => void;
}

export const GuruRecommendationsSection: React.FC<GuruRecommendationsSectionProps> = ({
  title = 'Recommends',
  subtitle = 'Curated by world-class mentors',
  recommendations,
  onGuruPress,
}) => {
  const navigation = useNavigation();

  const handleGuruPress = (guru: GuruRecommendationWithBooks) => {
    if (onGuruPress) {
      onGuruPress(guru);
    } else {
      // Fallback: navigate to first book
      if (guru.books.length > 0) {
        const book = guru.books[0];
        const bookId = book.id.includes('_') ? book.id.split('_')[0] : book.id;
        // @ts-ignore - Navigation types
        navigation.navigate('BookDetail', {
          bookId,
          languageCode: book.languageCode || 'en',
        });
      }
    }
  };

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text variant="h3" style={styles.title}>{title}</Text>
        {subtitle && (
          <Text variant="caption" style={styles.subtitle}>
            {subtitle}
          </Text>
        )}
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {recommendations.map((guru, index) => (
          <GuruRecommendationCard
            key={guru.id}
            name={guru.name}
            imageUrl={guru.imageUrl}
            title={guru.title}
            subtitle={guru.subtitle}
            books={guru.books}
            bookCount={guru.bookCount}
            insightCount={guru.insightCount}
            loading={guru.loading}
            onPress={() => handleGuruPress(guru)}
          />
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 40,
  },
  header: {
    paddingHorizontal: Theme.spacing.lg,
    marginBottom: Theme.spacing.md,
  },
  title: {
    color: 'rgb(0, 71, 89)',
  },
  subtitle: {
    marginTop: 2,
    color: 'rgb(0, 71, 89)',
  },
  scrollContent: {
    paddingHorizontal: Theme.spacing.lg,
    gap: Theme.spacing.md,
    paddingBottom: 4, // Extra padding to prevent shadow clipping
  },
});

